import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import Image from 'next/image'
import Link from 'next/link'
import {useRouter} from 'next/router'
import {useLinkResolver} from 'prismicio'
import {getButtonsData} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import type {Context} from 'src/common/types'
import {getLanguageCode, LEAD_FUNNEL_URL_PRODUCTION} from 'src/common/utils/locale'

export type ComparisonPageProps = SliceComponentProps<Content.ComparisonPageSlice>

const CheckMoss = () => (
  <span
    aria-hidden="true"
    className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-green-neon text-[11px] font-bold text-moss-black"
  >
    ✓
  </span>
)

const CheckCompetitor = () => (
  <span
    aria-hidden="true"
    className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-moss-grey-200 text-[11px] font-bold text-moss-black"
  >
    ✓
  </span>
)

const Cross = () => (
  <span
    aria-hidden="true"
    className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-black/[0.08] text-[11px] text-black/55"
  >
    —
  </span>
)

const MossCell = ({
  label,
  detail,
  iconType = 'check',
}: {
  label: string
  detail?: string | null
  iconType?: 'check' | 'cross'
}) => (
  <div className="border-l border-black/[0.06] bg-green-neon/[0.07] px-2 py-[22px] md:px-7">
    <div className="flex flex-col items-center gap-1.5 md:flex-row md:items-center md:gap-2">
      {iconType === 'cross' ? <Cross /> : <CheckMoss />}
      <span
        className={`text-[13px] font-medium md:text-[15px] ${
          iconType === 'cross' ? 'text-black/55' : 'text-moss-black'
        }`}
      >
        {label}
      </span>
    </div>
    {detail && <div className="mt-1 text-[12px] text-black/60">{detail}</div>}
  </div>
)

const CompetitorCell = ({
  label,
  detail,
  iconType = 'check',
}: {
  label: string
  detail?: string | null
  iconType?: 'check' | 'cross'
}) => (
  <div className="border-l border-black/[0.06] px-2 py-[22px] md:px-7">
    <div className="flex flex-col items-center gap-1.5 md:flex-row md:items-center md:gap-2">
      {iconType === 'cross' ? <Cross /> : <CheckCompetitor />}
      <span
        className={`text-[13px] font-medium md:text-[15px] ${
          iconType === 'cross' ? 'text-black/55' : 'text-moss-black'
        }`}
      >
        {label}
      </span>
    </div>
    {detail && <div className="mt-1 text-[12px] text-black/60">{detail}</div>}
  </div>
)

const FeatureCell = ({label, src}: {label: string; src: string}) => (
  <div className="flex flex-col gap-1 px-2 py-[22px] text-[15px] font-medium text-moss-black md:px-7">
    {label}
    <span className="text-[12px] font-normal text-black/45">{src}</span>
  </div>
)

const GroupRow = ({dotClass, label}: {dotClass: string; label: string}) => (
  <div className="flex items-center gap-[10px] border-t border-black/[0.08] bg-grey-120 px-7 py-[18px] text-[11px] font-semibold uppercase tracking-widest text-moss-black">
    <span className={cn('size-2 shrink-0 rounded-full', dotClass)} />
    {label}
  </div>
)

// SVG icons for at-a-glance feature cards (hardcoded, no Prismic field for icons)
const FEATURE_ICONS = [
  <svg key="cards" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="3" y="3" width="6" height="6" rx="1.5" fill="rgba(0,0,0,0.4)" />
    <rect x="11" y="3" width="6" height="6" rx="1.5" fill="rgba(0,0,0,0.4)" />
    <rect x="3" y="11" width="6" height="6" rx="1.5" fill="rgba(0,0,0,0.4)" />
    <rect x="11" y="11" width="6" height="6" rx="1.5" fill="rgba(0,0,0,0.4)" />
  </svg>,
  <svg key="check" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="7" stroke="rgba(0,0,0,0.4)" strokeWidth="1.5" />
    <path d="M7 10l2 2 4-4" stroke="rgba(0,0,0,0.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
  <svg key="globe" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="2" fill="rgba(0,0,0,0.4)" />
    <path d="M10 3v2M10 15v2M3 10h2M15 10h2" stroke="rgba(0,0,0,0.4)" strokeWidth="1.5" strokeLinecap="round" />
    <path
      d="M5.5 5.5l1.4 1.4M13.1 13.1l1.4 1.4M5.5 14.5l1.4-1.4M13.1 6.9l1.4-1.4"
      stroke="rgba(0,0,0,0.4)"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>,
  <svg key="people" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M6 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM14 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
      stroke="rgba(0,0,0,0.4)"
      strokeWidth="1.5"
    />
    <path
      d="M2 18c0-2.2 1.8-4 4-4h8c2.2 0 4 1.8 4 4"
      stroke="rgba(0,0,0,0.4)"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>,
]

const TABLE_COLS = 'minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1fr)'

const ComparisonPage = ({
  slice,
  context: {pageUid, buttonsData},
}: ComparisonPageProps & {context: Context}): JSX.Element => {
  const {linkResolver} = useLinkResolver()
  const domainCountry = useGetDomainCountry()
  const {locale} = useRouter()
  const lang = getLanguageCode(locale)

  const {primaryButtonData: buttonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.book_an_intro_button,
  })

  const buttonHref = buttonData?.link
    ? linkResolver(buttonData.link, 'comparison-cta')
    : `${LEAD_FUNNEL_URL_PRODUCTION}?lang=${lang}`

  const buttonLabel = buttonData?.label || 'Book a demo'

  const trackButtonClick = (location: string) => {
    trackMossAnalytics({
      event: 'interaction',
      interaction: {
        element: buttonData?.marketing_id || location,
        elementText: buttonLabel,
        location,
        type: 'click',
      },
      page: {
        domainCountry,
        type: pageUid || 'unknown',
      },
    })
  }

  const p = slice.primary
  const competitorName = p.competitor_name || 'Competitor'

  return (
    <div
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      data-nav-dark="true"
      className="bg-green-dark text-moss-white antialiased"
    >
      {/* ========== At-a-glance strip ========== */}
      <section className="bg-moss-grey-100 pb-24 pt-20 text-moss-black">
        <div className="mx-auto max-w-[1280px] px-10">
          {p.at_a_glance_eyebrow && (
            <div className="mb-12">
              <p className="mb-4 text-[11px] font-semibold uppercase tracking-widest text-black/45">
                {p.at_a_glance_eyebrow}
              </p>
              <h4 className="text-h4 md:text-h3 text-moss-black">{p.at_a_glance_eyebrow}</h4>
            </div>
          )}
          <div className="grid grid-cols-2 gap-12 lg:grid-cols-4">
            {p.features.map((item, i) => (
              <div key={i}>
                <div
                  className="mb-4 flex size-10 items-center justify-center rounded-[10px]"
                  style={{background: 'rgba(0,0,0,0.06)'}}
                >
                  {FEATURE_ICONS[i]}
                </div>
                <div className="mb-2 max-w-[180px] text-[20px] font-medium leading-[1.3]">{item.title}</div>
                <div className="text-[14px] leading-relaxed text-black/55">{item.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== Comparison table ========== */}
      <section className="bg-moss-grey-100 pb-24 pt-20 text-moss-black" id="compare">
        <div className="mx-auto max-w-[1280px] px-10">
          <div className="mb-12 max-w-[720px]">
            {p.table_eyebrow && (
              <p className="text-[11px] font-semibold uppercase tracking-widest text-green-deep">{p.table_eyebrow}</p>
            )}
            {p.table_heading && (
              <h2 className="text-h3 md:text-h2 mb-5 mt-4 text-balance text-moss-black">{p.table_heading}</h2>
            )}
            {p.table_subtext && <p className="text-[19px] leading-relaxed text-black/65">{p.table_subtext}</p>}
          </div>

          <div className="overflow-visible rounded-xl border border-black/[0.08] bg-moss-white">
            {/* Column headers */}
            <div
              className="sticky top-[68px] z-10 grid rounded-t-xl bg-moss-black text-moss-white shadow-md"
              style={{gridTemplateColumns: TABLE_COLS}}
            >
              <div
                className="flex items-center px-2 py-[22px] text-[11px] font-semibold uppercase tracking-widest md:px-7"
                style={{color: 'rgba(255,255,255,0.55)'}}
              >
                Feature
              </div>
              <div
                className="flex flex-col items-start gap-3 border-l px-2 py-[22px] md:flex-row md:items-center md:px-7"
                style={{borderColor: 'rgba(255,255,255,0.10)'}}
              >
                <div className="flex items-center gap-1 text-[18px] font-medium text-moss-white md:gap-[10px]">
                  <Image src="/icons/moss-icon-white.svg" alt="" height={18} width={18} />
                  <span>Moss</span>
                </div>
                <Link
                  href={buttonHref}
                  className="hidden items-center whitespace-nowrap rounded bg-green-neon px-4 py-2 text-[10px] font-semibold uppercase tracking-widest text-moss-black transition-opacity hover:opacity-85 md:inline-flex"
                  onClick={() => trackButtonClick('comparison-table-header')}
                >
                  {buttonLabel}
                </Link>
              </div>
              <div
                className="flex items-center border-l px-2 py-[22px] text-[18px] font-medium text-moss-white md:px-7"
                style={{borderColor: 'rgba(255,255,255,0.10)'}}
              >
                {competitorName}
              </div>
            </div>

            {/* Group: Moss goes further */}
            {p.table_rows_moss_advantage.length > 0 && (
              <>
                {p.table_rows_moss_advantage_label && (
                  <GroupRow dotClass="bg-green-neon" label={p.table_rows_moss_advantage_label} />
                )}
                {p.table_rows_moss_advantage.map((item, i) => (
                  <div key={i} className="grid border-t border-black/[0.06]" style={{gridTemplateColumns: TABLE_COLS}}>
                    <FeatureCell label={item.title || ''} src={item.subtitle || ''} />
                    <MossCell
                      label={item.moss_evaluation || ''}
                      detail={item.moss_detail}
                      iconType={item.moss_icon_type ?? 'check'}
                    />
                    <CompetitorCell
                      label={item.competitor_evaluation || ''}
                      detail={item.competitor_detail}
                      iconType={item.competitor_icon_type ?? 'check'}
                    />
                  </div>
                ))}
              </>
            )}

            {/* Group: Shared */}
            {p.table_rows_shared.length > 0 && (
              <>
                {p.table_rows_shared_label && <GroupRow dotClass="bg-grey-200" label={p.table_rows_shared_label} />}
                {p.table_rows_shared.map((item, i) => (
                  <div key={i} className="grid border-t border-black/[0.06]" style={{gridTemplateColumns: TABLE_COLS}}>
                    <FeatureCell label={item.title || ''} src={item.subtitle || ''} />
                    <MossCell
                      label={item.moss_evaluation || ''}
                      detail={item.moss_detail}
                      iconType={item.moss_icon_type ?? 'check'}
                    />
                    <CompetitorCell
                      label={item.competitor_evaluation || ''}
                      detail={item.competitor_detail}
                      iconType={item.competitor_icon_type ?? 'check'}
                    />
                  </div>
                ))}
              </>
            )}

            {/* Group: Competitor edge */}
            {p.table_rows_competitor_advantage.length > 0 && (
              <>
                {p.table_rows_competitor_advantage_label && (
                  <GroupRow dotClass="bg-orange-300" label={p.table_rows_competitor_advantage_label} />
                )}
                {p.table_rows_competitor_advantage.map((item, i) => (
                  <div key={i} className="grid border-t border-black/[0.06]" style={{gridTemplateColumns: TABLE_COLS}}>
                    <FeatureCell label={item.title || ''} src={item.subtitle || ''} />
                    <MossCell
                      label={item.moss_evaluation || ''}
                      detail={item.moss_detail}
                      iconType={item.moss_icon_type ?? 'check'}
                    />
                    <CompetitorCell
                      label={item.competitor_evaluation || ''}
                      detail={item.competitor_detail}
                      iconType={item.competitor_icon_type ?? 'check'}
                    />
                  </div>
                ))}
              </>
            )}
          </div>

          {isFilled.richText(p.table_footnote) && (
            <div className="mt-6 max-w-[880px] text-[12px] leading-relaxed text-black/45">
              <PrismicRichText field={p.table_footnote} />
            </div>
          )}
        </div>
      </section>

      {/* ========== Deep-dive cards ========== */}
      <section className="bg-green-dark py-16 text-moss-white md:py-24">
        <div className="mx-auto max-w-[1280px] px-10">
          {p.closer_look_eyebrow && (
            <p className="text-[11px] font-semibold uppercase tracking-widest text-green-neon">
              {p.closer_look_eyebrow}
            </p>
          )}
          {p.closer_look_heading && (
            <h2 className="text-h3 md:text-h2 mb-5 mt-4 text-moss-white">{p.closer_look_heading}</h2>
          )}
          {p.closer_look_subtext && (
            <p className="mb-14 max-w-[680px] text-[19px] leading-relaxed" style={{color: 'rgba(255,255,255,0.70)'}}>
              {p.closer_look_subtext}
            </p>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {p.closer_look_cards.map((card, i) => (
              <article
                key={i}
                className="flex flex-col rounded-xl p-9"
                style={{background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)'}}
              >
                {card.num && (
                  <div className="mb-6 inline-flex items-center gap-2 text-[13px] font-semibold text-green-neon">
                    <span className="h-px w-6 bg-green-neon" />
                    {card.num}
                  </div>
                )}
                <h3 className="text-h3 mb-4 text-moss-white">{card.title}</h3>
                {card.description && (
                  <p className="text-[16px] leading-[1.55]" style={{color: 'rgba(255,255,255,0.70)'}}>
                    {card.description}
                  </p>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ========== Final CTA ========== */}
      <section className="relative overflow-hidden bg-green-dark py-16 text-center md:py-24">
        <div
          className="pointer-events-none absolute inset-0"
          style={{background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(180,255,68,0.10), transparent 70%)'}}
        />
        <div className="relative mx-auto max-w-[1280px] px-10">
          {p.final_cta_eyebrow && (
            <span className="mb-6 block text-[11px] font-semibold uppercase tracking-widest text-green-neon">
              {p.final_cta_eyebrow}
            </span>
          )}
          <h2
            className="mb-8 font-light leading-[0.9] tracking-[-0.02em] text-moss-white"
            style={{fontSize: 'clamp(56px, 8vw, 120px)'}}
          >
            {p.final_cta_heading}
            {p.final_cta_heading_accent && (
              <>
                <br />
                <span className="text-green-neon">{p.final_cta_heading_accent}</span>
              </>
            )}
          </h2>
          {p.final_cta_subtext && (
            <p
              className="mx-auto mb-10 max-w-[560px] text-[20px] leading-relaxed"
              style={{color: 'rgba(255,255,255,0.70)'}}
            >
              {p.final_cta_subtext}
            </p>
          )}
          <Link
            href={buttonHref}
            className="inline-flex items-center rounded bg-green-neon px-5 py-3 text-[11px] font-semibold uppercase tracking-widest text-moss-black transition-opacity hover:opacity-85"
            onClick={() => trackButtonClick('comparison-final-cta')}
          >
            {buttonLabel}
          </Link>
        </div>
      </section>
    </div>
  )
}

export default ComparisonPage
